<template>
  <div class="message-input-container">
    <div v-if="!enabled" class="input-disabled-message">
      <i class="fa fa-lock"></i>
      {{ disabledMessage }}
    </div>
    <div v-else class="message-input-wrapper">
      <textarea
        ref="messageInput"
        v-model="message"
        class="form-control message-textarea"
        placeholder="Type your message to the customer..."
        rows="3"
        :disabled="sending"
        @keydown.ctrl.enter="sendMessage"
        @input="saveDraft"
      ></textarea>
      <div class="input-footer">
        <div class="character-counter" :class="{ 'text-danger': isOverLimit }">
          {{ message.length }} / {{ maxLength }}
        </div>
        <button
          class="btn btn-primary btn-send"
          :disabled="!canSend"
          @click="sendMessage"
        >
          <i class="fa" :class="sending ? 'fa-spinner fa-spin' : 'fa-paper-plane'"></i>
          {{ sending ? 'Sending...' : 'Send' }}
        </button>
      </div>
      <div class="input-hints">
        <small class="text-muted">
          <i class="fa fa-info-circle"></i>
          Press Ctrl+Enter to send
        </small>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MessageInput',
  props: {
    enabled: {
      type: Boolean,
      default: false,
    },
    disabledMessage: {
      type: String,
      default: 'Take over the conversation to send messages',
    },
    conversationId: {
      type: Number,
      required: true,
    },
    maxLength: {
      type: Number,
      default: 1000,
    },
  },
  data() {
    return {
      message: '',
      sending: false,
    };
  },
  computed: {
    canSend() {
      return this.message.trim().length > 0 && !this.sending && !this.isOverLimit;
    },
    isOverLimit() {
      return this.message.length > this.maxLength;
    },
    draftKey() {
      return `chatbot_draft_${this.conversationId}`;
    },
  },
  methods: {
    async sendMessage() {
      if (!this.canSend) return;

      const messageContent = this.message.trim();
      this.$emit('send', messageContent);

      // Clear message and draft
      this.message = '';
      this.clearDraft();

      // Focus back to input
      this.$nextTick(() => {
        if (this.$refs.messageInput) {
          this.$refs.messageInput.focus();
        }
      });
    },
    saveDraft() {
      try {
        localStorage.setItem(this.draftKey, this.message);
      } catch (error) {
        console.error('Failed to save draft:', error);
      }
    },
    loadDraft() {
      try {
        const draft = localStorage.getItem(this.draftKey);
        if (draft) {
          this.message = draft;
        }
      } catch (error) {
        console.error('Failed to load draft:', error);
      }
    },
    clearDraft() {
      try {
        localStorage.removeItem(this.draftKey);
      } catch (error) {
        console.error('Failed to clear draft:', error);
      }
    },
  },
  mounted() {
    this.loadDraft();
    if (this.enabled && this.$refs.messageInput) {
      this.$refs.messageInput.focus();
    }
  },
  watch: {
    enabled(newVal) {
      if (newVal && this.$refs.messageInput) {
        this.$nextTick(() => {
          this.$refs.messageInput.focus();
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.message-input-container {
  padding-top: 1rem;
}

.input-disabled-message {
  text-align: center;
  padding: 1rem;
  border-radius: 0.5rem;
  color: #6c757d;

  i {
    margin-right: 0.5rem;
    font-size: 1.25rem;
  }
}

.message-input-wrapper {
  .message-textarea {
    resize: vertical;
    min-height: 80px;
    max-height: 200px;
    border-radius: 0.5rem;
    transition: border-color 0.2s;

    &:focus {
      border-color: #007bff;
      box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    }

    &:disabled {
      cursor: not-allowed;
    }
  }

  .input-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 0.75rem;
  }

  .character-counter {
    font-size: 0.875rem;
    color: #6c757d;

    &.text-danger {
      font-weight: 600;
    }
  }

  .btn-send {
    min-width: 100px;

    i {
      margin-right: 0.5rem;
    }
  }

  .input-hints {
    margin-top: 0.5rem;
  }
}
</style>
